/**
 * Ratings Page
 *
 * Entry point for viewing institution ratings.
 * Shows the RatingsMap when no institution is selected.
 * Shows the InstitutionDashboard when an institution is selected.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useInstitution } from '../../context/InstitutionContext';
import RatingsMap from '../RatingsMap';
import InstitutionDashboard from '../InstitutionDashboard';
import './Ratings.css';

interface PeriodInfo {
  label: string;
  date: string;
  isLatest: boolean;
  onReset: () => void;
}

export default function Ratings() {
  const { selectedInstitution, setSelectedInstitution } = useInstitution();
  const { t } = useTranslation();
  const [periodInfo, setPeriodInfo] = useState<PeriodInfo | null>(null);

  const handlePeriodChange = useCallback((info: PeriodInfo) => {
    setPeriodInfo(info);
  }, []);

  // Show map when no institution is selected
  if (!selectedInstitution) {
    return <RatingsMap />;
  }

  // Show dashboard with back button when institution is selected
  return (
    <div className="ratings-dashboard-view">
      <div className="ratings-back-header">
        <button
          className="back-to-map-btn"
          onClick={() => setSelectedInstitution(null)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {t('ratings.backToMap')}
        </button>
        <span className="selected-institution-name">
          {selectedInstitution.name}
        </span>
        {periodInfo && (
          <div className="period-badge-container">
            <span
              className={`period-badge ${periodInfo.isLatest ? 'current' : 'historical'}`}
            >
              <span className="period-label">{periodInfo.label}</span>
              <span className="period-date">{periodInfo.date}</span>
            </span>
            {!periodInfo.isLatest && (
              <button className="reset-period-btn" onClick={periodInfo.onReset}>
                {'↺ '}{t('ratings.resetPeriod')}
              </button>
            )}
          </div>
        )}
      </div>
      <InstitutionDashboard
        hideHeader
        onPeriodChange={handlePeriodChange}
      />
    </div>
  );
}
